import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { MessageCircle, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

import { API, authFetch } from '../lib/api';

export const CommentSection = ({ articleId, comments, onCommentAdded, onCommentDeleted }) => {
  const { user, login } = useAuth();
  const [newComment, setNewComment] = useState('');
  const [replyTo, setReplyTo] = useState(null);
  const [replyContent, setReplyContent] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(String(dateString).replace(' ', 'T'));
    if (isNaN(date.getTime())) return '';
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const handleSubmitComment = async (e) => {
    e.preventDefault();
    
    if (!user) {
      login();
      return;
    }

    if (!newComment.trim()) return;

    setIsSubmitting(true);
    try {
      const response = await authFetch(`${API}/articles/${articleId}/comments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content: newComment.trim() })
      });

      if (response.ok) {
        const comment = await response.json();
        onCommentAdded({ ...comment, replies: [] });
        setNewComment('');
        toast.success('Comment added');
      } else {
        toast.error('Failed to add comment');
      }
    } catch (error) {
      toast.error('Failed to add comment');
    }
    setIsSubmitting(false);
  };

  const handleSubmitReply = async (parentId) => {
    if (!user) {
      login();
      return;
    }

    if (!replyContent.trim()) return;

    setIsSubmitting(true);
    try {
      const response = await authFetch(`${API}/articles/${articleId}/comments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content: replyContent.trim(), parent_id: parentId })
      });

      if (response.ok) {
        const reply = await response.json();
        onCommentAdded(reply, parentId);
        setReplyContent('');
        setReplyTo(null);
        toast.success('Reply added');
      } else {
        toast.error('Failed to add reply');
      }
    } catch (error) {
      toast.error('Failed to add reply');
    }
    setIsSubmitting(false);
  };

  const handleDeleteComment = async (commentId) => {
    try {
      const response = await authFetch(`${API}/comments/${commentId}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        onCommentDeleted(commentId);
        toast.success('Comment deleted');
      } else {
        toast.error('Failed to delete comment');
      }
    } catch (error) {
      toast.error('Failed to delete comment');
    }
  };

  const renderComment = (comment, isReply) => (
    <div key={comment.comment_id} className={isReply ? 'ml-12' : ''} data-testid={`comment-${comment.comment_id}`}>
      <div className="flex gap-3">
        <Avatar className="w-8 h-8 flex-shrink-0">
          <AvatarImage src={comment.author_picture} alt={comment.author_name} />
          <AvatarFallback className="text-xs bg-muted">
            {getInitials(comment.author_name)}
          </AvatarFallback>
        </Avatar>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="font-medium text-sm">{comment.author_name}</span>
            <span className="text-xs text-muted-foreground">{formatDate(comment.created_at)}</span>
          </div>
          
          <p className="text-sm text-foreground/90 whitespace-pre-wrap">{comment.content}</p>
          
          <div className="flex items-center gap-2 mt-2">
            {!isReply && (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-xs text-muted-foreground hover:text-foreground"
                onClick={() => setReplyTo(replyTo === comment.comment_id ? null : comment.comment_id)}
              >
                <MessageCircle className="w-3.5 h-3.5 mr-1" />
                Reply
              </Button>
            )}
            {user && user.user_id === comment.author_id && (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-xs text-destructive hover:text-destructive"
                onClick={() => handleDeleteComment(comment.comment_id)}
                data-testid={`delete-comment-${comment.comment_id}`}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            )}
          </div>

          {replyTo === comment.comment_id && (
            <div className="mt-3" data-testid={`reply-form-${comment.comment_id}`}>
              <Textarea
                value={replyContent}
                onChange={(e) => setReplyContent(e.target.value)}
                placeholder="Write a reply..."
                className="min-h-[60px] text-sm"
              />
              <div className="flex gap-2 mt-2">
                <Button
                  size="sm"
                  onClick={() => handleSubmitReply(comment.comment_id)}
                  disabled={isSubmitting || !replyContent.trim()}
                  className="rounded-full"
                >
                  Reply
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => { setReplyTo(null); setReplyContent(''); }}
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {comment.replies && comment.replies.length > 0 && (
        <div className="mt-4 space-y-4">
          {comment.replies.map(reply => renderComment(reply, true))}
        </div>
      )}
    </div>
  );

  return (
    <div className="mt-12 pt-8 border-t border-border" data-testid="comment-section">
      <h3 className="text-xl font-bold mb-6" style={{ fontFamily: 'Fraunces, serif' }}>
        Responses ({comments.length})
      </h3>

      <form onSubmit={handleSubmitComment} className="mb-8">
        <div className="flex gap-3">
          {user ? (
            <Avatar className="w-10 h-10 flex-shrink-0">
              <AvatarImage src={user.picture} alt={user.name} />
              <AvatarFallback className="bg-accent text-accent-foreground">
                {getInitials(user.name)}
              </AvatarFallback>
            </Avatar>
          ) : (
            <Avatar className="w-10 h-10 flex-shrink-0">
              <AvatarFallback className="bg-muted">?</AvatarFallback>
            </Avatar>
          )}
          
          <div className="flex-1">
            <Textarea
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder={user ? "What are your thoughts?" : "Sign in to comment..."}
              className="min-h-[100px] mb-3"
              onClick={() => !user && login()}
              data-testid="new-comment-input"
            />
            <Button
              type="submit"
              disabled={isSubmitting || !newComment.trim()}
              className="rounded-full"
              data-testid="submit-comment-btn"
            >
              {user ? 'Respond' : 'Sign in to respond'}
            </Button>
          </div>
        </div>
      </form>

      <div className="space-y-6">
        {comments.map(comment => renderComment(comment, false))}
        
        {comments.length === 0 && (
          <p className="text-center text-muted-foreground py-8">
            No responses yet. Be the first to respond.
          </p>
        )}
      </div>
    </div>
  );
};

export default CommentSection;
